import './charts.less';
import React from 'react';
import { Bar, defaults } from '../../../../../../shared/js/react-chartjs/react-chartjs-2';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import moment from 'moment';
import cn from 'classname';

// shared
import SwitchCharts from '../../../../../modules/shared/switch';
import { formatMoney } from '../../../../../modules/shared/format';
import { getCurrencyIcon } from '../../../../../../shared/js/get-currency-icon';

const goalsColor = 'rgba(42, 66, 107, 0.5)';
const hostsColor = 'rgba(8, 177, 196, 0.5)';
const goalsHoverColor = 'rgb(42, 66, 107)';
const hostsHoverColor = 'rgb(8, 177, 196)';

defaults.global.animation = false;
defaults.global.legend = { display: false };
defaults.global.hover = {
	mode: 'x',
	intersect: false
};

let suggestedMaxTickForConversions = 0;
let currentCharType = 'week-chart';

const Charts = props => {
	const { intl } = props;
	currentCharType = props.routes[props.routes.length-1].name;
	const data = props.charts.data;
	const totals = calculateConversionsAndProfit(data);
	const summaryData = currentCharType == 'day-chart' ? getDayStats(totals, intl) : getWeekStats(totals, intl);

	window.onresize = props.onWindowResize;

	const switchChartsLinkConfig = [{
		className: 'header-switch__link-dashboard-week-chart',
		title: intl('За неделю'),
		url: 'dashboard/week-chart/'
	}, {
		className: 'header-switch__link-dashboard-day-chart',
		title: intl('За день'),
		url: 'dashboard/day-chart/'
	}];

	const currencyIconClass = `icon-currency ${props.user.activeBalance}`;

	return (<div className={cn('chart', props.className)}>
		<div className="container__content-title">
			<h2 className="font-proxima-nova-bold">{intl('Сводная статистика')}</h2>
			<SwitchCharts links={switchChartsLinkConfig} intl={intl} onSwitch={props.onChartsSwitch}/>
			{props.user.general &&
					props.user.general.is_master && (
				<div className="header-switch">
					<div className="header-switch__links font-proxima-nova-semibold">
						<a
							href="#"
							className={cn({
								'header-switch__link-my-stats': true,
								active: props.masterAccountDisplayStatsConfig === 'master_only'
							})}
							onClick={e => {
								e.preventDefault();
								props.onTypeSwitch('master_only');
							}}
						>
							{intl('Моя')}
						</a>
						<a
							href="#"
							className={cn({
								'header-switch__link-children-stats': true,
								active: props.masterAccountDisplayStatsConfig === 'children_only'
							})}
							onClick={e => {
								e.preventDefault();
								props.onTypeSwitch('children_only');
							}}
						>
							{intl('Вебмастеров')}
						</a>
					</div>
				</div>
			)}
		</div>

		<div className="chart__body">
			<div id="catBg" />
			{ currentCharType == 'day-chart' ? (<Bar key='chart1'
				data={getDayChart(data, intl)}
				options={chartOptions('day-chart', intl)}
				width={715}
				height={300}
			/>)
				: (<Bar key='chart2'
					data={getWeekChart(data, intl)}
					options={chartOptions('week-chart', intl)}
					width={715}
					height={300}
				/>)}

		</div>
		<div className="chart__footer">
			<div className="info">
				<span className="info-title">
					<span>{intl('Конверсии')}</span>
					<span>{summaryData.period}</span>
				</span>

				<span className="info-amount">
					{summaryData.conversions}
				</span>
			</div>

			<div className="separator-border"/>

			<div className="info">
				<span className="info-title">
					<span>{intl('Заработок')}</span>
					<span>{summaryData.period}</span>
				</span>

				<span className="info-amount">
					<span>{formatMoney(summaryData.revenue)} </span>
					<span className={currencyIconClass}>
						{getCurrencyIcon(props.user.activeBalance)}
					</span>
				</span>
			</div>

			<div className="graphic-info">
				<span className="graphic-info__hosts">{intl('Хосты')}</span>
				<span className="graphic-info__leads">{intl('Конверсии')}</span>
			</div>

		</div>
		<div id="hideOutlineBottom"></div>
	</div>);
};

Charts.propTypes = {
	className: PropTypes.string,
	routes: PropTypes.array,
	charts: PropTypes.object,
	user: PropTypes.object,
	onChartsSwitch: PropTypes.func,
	onTypeSwitch: PropTypes.func,
	masterAccountDisplayStatsConfig: PropTypes.string,
	onWindowResize: PropTypes.func,
	intl: PropTypes.func
};

function getDayChart(data, intl) {
	const dataConversions =
		(data && data.dayChart) ?
			map(data.dayChart, it => +it.accepted_conversions) :
			[0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
	const dataHosts = (data && data.dayChart) ?
		map(data.dayChart, it => +it.hosts) : [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

	const suggestedMax = Math.max.apply(Math, dataConversions) * 2;
	suggestedMaxTickForConversions = suggestedMax >= 10 ? suggestedMax : 10;

	return data && {
		labels: map(data.dayChart, it => moment().hours(it.hour).format('HH')),
		datasets: [
			{
				...hostsDatasetOptions(intl),
				data: dataHosts
			},
			{
				...conversionsDatasetOptions(intl),
				data: dataConversions
			}
		]
	};
}

function getWeekChart(data, intl) {
	const dataConversions = (data && data.weekChart) ?
		map(data.weekChart, it => +it.accepted_conversions) : [0, 0, 0, 0, 0, 0, 0];
	const dataHosts = (data && data.weekChart) ?
		map(data.weekChart, it => +it.hosts) : [0, 0, 0, 0, 0, 0, 0];

	const suggestedMax = Math.max.apply(Math, dataConversions) * 2;
	suggestedMaxTickForConversions = suggestedMax >= 10 ? suggestedMax : 10;

	return {
		labels: map(data.weekChart, it => moment(it.date).format('DD MMMM')),
		datasets: [
			{
				...hostsDatasetOptions(intl),
				data: dataHosts
			},
			{
				...conversionsDatasetOptions(intl),
				data: dataConversions
			}
		]
	};
}

function getDayStats(totals, intl) {
	return totals && {
		period: intl('за день'),
		conversions: totals.todayConversions,
		revenue: Number(totals.todayPayout).toFixed(0)
	};
}

function getWeekStats(totals, intl) {
	return totals && {
		period: intl('за неделю'),
		conversions: totals.totalConversions,
		revenue: Number(totals.totalPayout).toFixed(0)
	};
}

function calculateConversionsAndProfit (data) {
	return {
		todayConversions: reduce(data.dayChart, (result, it) => result + +it.accepted_conversions, 0),
		totalConversions: reduce(data.weekChart, (result, it) => result + +it.accepted_conversions, 0),
		todayPayout: reduce(data.dayChart, (result, it) => result + +it.accepted_income, 0),
		totalPayout: reduce(data.weekChart, (result, it) => result + +it.accepted_income, 0)
	};
}

const hostsDatasetOptions = intl => ({
	type: 'bar',
	label: intl('Хосты'),
	fill: false,
	backgroundColor: hostsColor,
	borderColor: hostsColor,
	hoverBackgroundColor: hostsHoverColor,
	hoverBorderColor: hostsColor,
	yAxisID: 'y-axis-1'
});

const conversionsDatasetOptions = intl => ({
	label: intl('Конверсии'),
	type:'bar',
	fill: false,
	backgroundColor: goalsColor,
	borderColor: goalsColor,
	hoverBackgroundColor: goalsHoverColor,
	hoverBorderColor: hostsColor,
	yAxisID: 'y-axis-2'
});

const chartOptions = (type, intl) => {
	const customTooltips = function(tooltip) {
		const catBg = document.getElementById('catBg');

		let tooltipEl = document.getElementById('chartjs-tooltip');
		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'chartjs-tooltip';
			tooltipEl.innerHTML = '<div id="tooltip_hosts"></div><div id="tooltip_goals"></div>';
			this._chart.canvas.parentNode.appendChild(tooltipEl);
		}

		let tooltipH = document.getElementById('chartjs-tooltip-h');
		let tooltipG = document.getElementById('chartjs-tooltip-g');
		if (!tooltipH) {
			tooltipH = document.createElement('div');
			tooltipH.id = 'chartjs-tooltip-h';
			tooltipH.innerHTML = '<div id="lineh" class="hosts"></div><div id="spansh"></div>';
			document.getElementById('chartjs-tooltip').parentNode.appendChild(tooltipH);
		}

		if (!tooltipG) {
			tooltipG = document.createElement('div');
			tooltipG.id = 'chartjs-tooltip-g';
			tooltipG.innerHTML = '<div id="lineg" class="goals"></div><div id="spansg"></div>';
			document.getElementById('chartjs-tooltip').parentNode.appendChild(tooltipG);
		}

		if (
			tooltip.opacity === 0
			|| (
				!tooltip.dataPoints
				|| tooltip.dataPoints.length < 2
				|| !tooltip.dataPoints[1].yLabel
				&& !tooltip.dataPoints[0].yLabel
			)
		) {
			tooltipEl.style.opacity = 0;
			tooltipH.style.opacity = 0;
			tooltipG.style.opacity = 0;
			catBg.style.opacity = 0;
			return;
		}

		tooltipEl.classList.remove('above', 'below', 'no-transform');
		if (tooltip.yAlign) {
			tooltipEl.classList.add(tooltip.yAlign);
		} else {
			tooltipEl.classList.add('no-transform');
		}

		const isForConversions = tooltip.caretX == tooltip.dataPoints[1].x;
		const divHosts = tooltipEl.querySelector('#tooltip_hosts');
		const divGoals = tooltipEl.querySelector('#tooltip_goals');

		if (tooltip.body) {
			const bodyLines = tooltip.body.map(it => it.lines[0]);
			bodyLines.forEach(function(body, i) {
				const el = !i ? divHosts : divGoals;
				const matchedValue = body.match(/(\d+)/);
				const value = formatMoney(matchedValue && matchedValue[0] && +matchedValue[0] || 0);
				body = body.replace(/\d+/, `&nbsp;<b>${value}</b>`);
				el.innerHTML = '<span>' + body + '</span>';
			});
		}

		const positionY = this._chart.canvas.offsetTop;
		const positionX = this._chart.canvas.offsetLeft;
		const leftAxisWidth = this._chart.controller.chartArea.left;

		const divLineH = tooltipH.querySelector('#lineh');
		const divLabelH = tooltipH.querySelector('#spansh');
		const divLineG = tooltipG.querySelector('#lineg');
		const divLabelG = tooltipG.querySelector('#spansg');
		const valueH = formatMoney(+tooltip.dataPoints[0].yLabel);
		const valueG = formatMoney(+tooltip.dataPoints[1].yLabel);

		divLabelH.innerHTML = `<span class="hosts-tooltip" >${valueH}</span>
			<span class="hosts-triangle"></span>`;
		divLabelG.innerHTML = `<span class="conversions-tooltip" >${valueG}</span>
			<span class="conversions-triangle"></span>`;

		catBg.style.opacity = 1;
		tooltipH.style.opacity = 1;
		tooltipH.style.position = 'absolute';
		tooltipH.style.top = positionY + tooltip.dataPoints[0].y + 'px';
		tooltipG.style.opacity = 1;
		tooltipG.style.position = 'absolute';
		tooltipG.style.top = positionY + tooltip.dataPoints[1].y + 'px';

		const spansWidthH = parseInt(window.getComputedStyle(
			tooltipH.querySelector(`#spansh .hosts-tooltip`)
		).width)	+ 14;
		const spansWidthG = parseInt(window.getComputedStyle(
			tooltipG.querySelector(`#spansg .conversions-tooltip`)
		).width)	+ 14;

		const minY = tooltip.dataPoints[0].y > tooltip.dataPoints[1].y ? tooltip.dataPoints[1].y : tooltip.dataPoints[0].y;
		const halfBarWidth = this._chart.controller.active['0']._view.width / 2;
		const categoryWidth = halfBarWidth*4 + halfBarWidth*4/3;

		tooltipEl.style.opacity = 1;
		const tooltipPosLeft = positionX + tooltip.caretX + (isForConversions ? -halfBarWidth : halfBarWidth);
		tooltipEl.style.left = tooltipPosLeft + 'px';
		tooltipEl.style.top = positionY + minY - tooltip.height - 15 + 'px';
		tooltipEl.style.fontSize = tooltip.fontSize;
		tooltipEl.style.fontStyle = tooltip._fontStyle;

		catBg.style.top = positionY + 'px';
		catBg.style.left = tooltipPosLeft - categoryWidth/2 + 'px';
		catBg.style.width = categoryWidth + 'px';
		catBg.style.height = this._chart.controller.chartArea.bottom + 'px';

		tooltipG.style.left = positionX + leftAxisWidth + 'px';
		divLineG.style.width = this._chart.controller.scales['x-axis-0'].width + spansWidthG + 'px';

		tooltipH.style.left = positionX + leftAxisWidth - spansWidthH + 'px';
		divLineH.style.width = this._chart.controller.scales['x-axis-0'].width + spansWidthH + 'px';
	};

	return ({
		maintainAspectRatio: false,
		responsive: false,
		animate: false,
		layout: {
			padding: {
				left: 7
			}
		},
		tooltips: {
			enabled: false,
			mode: 'index',
			intersect: false,
			position: 'nearest',
			custom: customTooltips
		},
		elements: {
			line: {
				fill: false
			}
		},
		scales: {
			xAxes: [{
				display: true,
				barPercentage: 1.0,
				categoryPercentage: 0.75,
				gridLines: {
					display: true,
					drawTicks: true,
					offsetGridLines: true
				},
				ticks: {
					fontSize: 10,
					fontColor: '#999'
				}
			}],
			yAxes: [{
				type: 'linear',
				display: true,
				position: 'left',
				id: 'y-axis-1',
				gridLines: {
					display: true,
					drawTicks: false
				},
				labels: {
					show: true
				},
				ticks: {
					beginAtZero:true,
					min: 0,
					suggestedMax: 10,
					callback: (value, index, values) => formatMoney(value),
					padding: 15,
					autoSkip: true,
					fontSize: 10,
					fontColor: '#999'
				},
				afterUpdate: updateHideOutlineBottom
			}, {
				display: true,
				position: 'right',
				id: 'y-axis-2',
				gridLines: {
					display: false
				},
				labels: {
					show: true
				},
				ticks: {
					beginAtZero:true,
					min: 0,
					max: suggestedMaxTickForConversions || 10,
					callback: (value, index, values) => formatMoney(value),
					padding: 15,
					autoSkip: true,
					fontSize: 10,
					fontColor: '#999'
				}
			}]
		}
	});
};

function updateHideOutlineBottom (scale) {
	const hider = document.getElementById('hideOutlineBottom');
	if (!hider || !scale || !scale.chart || !scale.chart.chartArea) {
		return;
	}
	const xaxis = scale.chart.scales['x-axis-0'];
	setTimeout(() => {
		hider.style.width = xaxis.width + 2 + 'px';
		hider.style.left = xaxis.ctx.canvas.offsetLeft
			&& (xaxis.ctx.canvas.offsetLeft + scale.chart.chartArea.left - 1 + 'px');
		hider.style.top = xaxis.ctx.canvas.offsetTop + xaxis.top + 0.5 + 'px';
	}, 10);

}

export default Charts;



// WEBPACK FOOTER //
// ./frontend/cabinets/apps/rocket/modules/dashboard/components/charts.js